import { z } from 'zod';
/**
 * ISO 27001-aligned information-classification surface.
 *
 * Orthogonal to the existing 3-ring trust model. Any chunk in any ring can
 * additionally carry a `classification` (data-sensitivity tier) and an
 * `accessGroups` list (which Entra-mapped groups can read it). Default for
 * everything existing is `internal` — the corpus is OneNebula employee
 * content; nothing is anonymous-public today.
 *
 * Day-one access-group taxonomy is intentionally minimal — `confidential`
 * only. Adding a new bucket (e.g. `boardroom`, `legal`) is a deliberate
 * code change here + new Entra group + matching admin chip.
 */
export const Classification = z.enum(['public', 'internal', 'confidential', 'restricted']);
export const AccessGroup = z.string().min(1).max(120);
export const PiiFlag = z.enum([
    'email_addresses',
    'phone_numbers',
    'salary_data',
    'contract_value',
    'employee_names',
    'customer_names',
]);
/**
 * Day-one set of recognised Entra groups → app-internal accessGroup names.
 * Keep this small. Adding a new confidential subset later is a code change
 * here + new Entra group + matching admin chip.
 */
export const KNOWN_ACCESS_GROUPS = ['confidential'];
/**
 * Sub-classification within `classification === 'confidential'` — purely
 * for display, filtering, and ops bookkeeping. Access is still gated by
 * `accessGroups`, which is unchanged. The set is closed so the admin chip
 * stays predictable; adding a fourth kind is a one-line code change.
 *
 *   - `internal` — OneNebula's own confidential material (board, strategy).
 *   - `vendor`   — supplier / partner contracts and proposals.
 *   - `client`   — customer contracts, revenue proposals, NDAs.
 */
export const ConfidentialKind = z.enum(['internal', 'vendor', 'client']);
/**
 * Sub-classification within `ring1` non-confidential ("Product") — splits
 * the corpus by OneNebula product line for display and filtering. Same
 * shape as ConfidentialKind: classification axis is orthogonal to access,
 * the chip uses brand colours per product. Add a fourth value here when
 * a new product ships.
 */
export const ProductKind = z.enum(['oneview', 'stactize', 'surge']);
/**
 * Shared refinement: `confidential` / `restricted` rows MUST carry at least
 * one access group, otherwise the chunks are invisible to every caller
 * (the agent retrieval filter drops them all). Public + internal stay
 * visibility-open by definition so they have no access-group requirement.
 */
export const CLASSIFICATION_REQUIRES_GROUP_MESSAGE = 'Confidential or Restricted classification requires at least one access group; otherwise the chunks become invisible to everyone.';
export function classificationRequiresGroup(c) {
    return c === 'confidential' || c === 'restricted';
}
/**
 * Retrieval/browse/download visibility gate — the single TS source of truth,
 * a mirror of the agent's `retrieve.py::is_visible`. `public`/`internal` are
 * open to any authenticated caller; `confidential`/`restricted` require the
 * caller's access groups to intersect the doc's. A confidential/restricted
 * row with no access groups is invisible to everyone (fail-closed; the admin
 * write route rejects this, this is defence in depth). A missing
 * classification reads as `internal`.
 *
 * Must be applied on EVERY surface that exposes corpus rows — not just the
 * agent retrieval. The /documents browse list + download routes were missing
 * it, leaking confidential Ring 0 titles to all authenticated users (2026-06).
 */
export function isClassificationVisible(classification, docAccessGroups, callerAccessGroups) {
    const cls = classification ?? 'internal';
    if (cls === 'public' || cls === 'internal')
        return true;
    const docGroups = docAccessGroups ?? [];
    if (docGroups.length === 0)
        return false;
    const caller = new Set(callerAccessGroups);
    return docGroups.some((g) => caller.has(g));
}
/**
 * Shape applied to chunks + registry rows. Fields are `.optional()` (not
 * `.default(...)`) so existing rows without them parse cleanly — the
 * read-time default at every consumer is `internal` / `[]` / `[]`. Making
 * them `.default(...)` would mint the field on every write, which is
 * harmless but inflates Firestore storage on legacy chunks; opting-in at
 * read time keeps the corpus shape stable.
 */
export const classificationFields = {
    classification: Classification.optional(),
    accessGroups: z.array(AccessGroup).max(20).optional(),
    piiFlags: z.array(PiiFlag).max(20).optional(),
    confidentialKind: ConfidentialKind.optional(),
    productKind: ProductKind.optional(),
};
/**
 * Shape applied to *source* rows (Confluence / CodeSource / upload init).
 * Prefixed `default*` because the source carries the cascade default for
 * docs ingested from it, not the doc-level field itself.
 */
export const sourceDefaultClassificationFields = {
    defaultClassification: Classification.optional(),
    defaultAccessGroups: z.array(AccessGroup).max(20).optional(),
    defaultConfidentialKind: ConfidentialKind.optional(),
    defaultProductKind: ProductKind.optional(),
};
/**
 * Read-time defaults — resolve a row's classification + accessGroups
 * regardless of whether the fields were ever set.
 */
export function readClassification(row) {
    return row.classification ?? 'internal';
}
export function readAccessGroups(row) {
    return row.accessGroups ?? [];
}
/**
 * Default to `internal` for any confidential row that pre-dates the field
 * (matches the "everything we used to call simply 'Confidential' was
 * OneNebula's own stuff" intent). Non-confidential rows have no kind.
 */
export function readConfidentialKind(row) {
    if (!classificationRequiresGroup(row.classification ?? 'internal'))
        return null;
    return row.confidentialKind ?? 'internal';
}
/**
 * Default to null for any row that isn't a product-tier doc. Product
 * docs without an explicit kind show the legacy generic "Product" chip
 * (caller decides) — we don't auto-tag a default here so existing rows
 * keep their current display until an operator picks.
 */
export function readProductKind(row) {
    if (classificationRequiresGroup(row.classification ?? 'internal'))
        return null;
    return row.productKind ?? null;
}
//# sourceMappingURL=classification.js.map